import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Users, Headphones, ArrowRight, Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accessType, setAccessType] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'client'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let user;
      if (authMode === 'login') {
        user = await login(formData.email, formData.password);
      } else {
        const role = accessType === 'backoffice' ? 'agent' : 'client';
        user = await register(formData.email, formData.password, formData.name, role);
      }

      toast.success(authMode === 'login' ? 'Connexion réussie' : 'Compte créé avec succès');
      
      // Redirect based on role
      if (['admin', 'moderator', 'agent'].includes(user.role)) {
        navigate('/backoffice');
      } else {
        navigate('/portal');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  if (!accessType) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 flex">
        {/* Left side - Image */}
        <div 
          className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1593586710811-c92ee75ddf6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMG9mZmljZSUyMGFyY2hpdGVjdHVyZSUyMGdsYXNzJTIwZ3JlZW58ZW58MHx8fHwxNzcwNjcxMjQ4fDA&ixlib=rb-4.1.0&q=85)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/95 to-zinc-900/90" />
          <div className="relative z-10 flex flex-col justify-center p-12">
            <div className="mb-8">
              <img 
                src="/logo-freelanceurs.png" 
                alt="Freelanceurs" 
                className="h-12 w-auto"
              />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Freelanceurs Ticketing Plateforme
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              Déposez et gérez efficacement vos demandes aux équipes support avec notre système de ticketing.
            </p>
          </div>
        </div>

        {/* Right side - Selection */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center lg:text-left">
              <div className="lg:hidden flex items-center justify-center mb-6">
                <div className="h-10 w-auto bg-zinc-900 dark:bg-transparent rounded-lg p-2 flex items-center">
                  <img 
                    src="/logo-freelanceurs.png" 
                    alt="Freelanceurs" 
                    className="h-full w-auto"
                  />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Bienvenue
              </h2>
              <p className="text-slate-600 dark:text-zinc-400">
                Choisissez votre type d'accès pour continuer
              </p>
            </div>

            <div className="grid gap-4">
              <button
                onClick={() => setAccessType('client')}
                data-testid="access-client-btn"
                className="group relative flex items-center gap-4 p-6 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-xl hover:border-emerald-500 hover:shadow-lg transition-all duration-200"
              >
                <div className="w-14 h-14 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                  <Users className="w-7 h-7 text-emerald-600 dark:text-emerald-400 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Espace Client
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">
                    Créez et suivez vos tickets de support
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
              </button>

              <button
                onClick={() => setAccessType('backoffice')}
                data-testid="access-backoffice-btn"
                className="group relative flex items-center gap-4 p-6 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-xl hover:border-emerald-500 hover:shadow-lg transition-all duration-200"
              >
                <div className="w-14 h-14 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center group-hover:bg-emerald-500 transition-colors">
                  <Headphones className="w-7 h-7 text-emerald-600 dark:text-emerald-400 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Équipe Support
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">
                    Gérez et traitez les demandes clients
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1593586710811-c92ee75ddf6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMG9mZmljZSUyMGFyY2hpdGVjdHVyZSUyMGdsYXNzJTIwZ3JlZW58ZW58MHx8fHwxNzcwNjcxMjQ4fDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/95 to-zinc-900/90" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="mb-8">
            <img 
              src="/logo-freelanceurs.png" 
              alt="Freelanceurs" 
              className="h-12 w-auto"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            {accessType === 'client' ? 'Espace Client' : 'Back Office'}
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            {accessType === 'client' 
              ? 'Créez des tickets et suivez l\'avancement de vos demandes en temps réel.'
              : 'Gérez les tickets clients, analysez les performances et collaborez efficacement.'
            }
          </p>
        </div>
      </div>

      {/* Right side - Auth form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md border-slate-200 dark:border-zinc-800">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setAccessType(null)}
                className="text-sm text-slate-500 dark:text-zinc-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
              >
                ← Retour
              </button>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                accessType === 'client' 
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              }`}>
                {accessType === 'client' ? 'Espace Client' : 'Back Office'}
              </span>
            </div>
            <CardTitle className="text-2xl font-bold">
              {authMode === 'login' ? 'Connexion' : 'Créer un compte'}
            </CardTitle>
            <CardDescription>
              {authMode === 'login' 
                ? 'Entrez vos identifiants pour accéder à votre espace'
                : 'Remplissez le formulaire pour créer votre compte'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={authMode} onValueChange={setAuthMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Connexion</TabsTrigger>
                <TabsTrigger value="register">Inscription</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="space-y-4">
                {authMode === 'register' && (
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom complet</Label>
                    <Input
                      id="name"
                      data-testid="auth-name-input"
                      placeholder="Jean Dupont"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={authMode === 'register'}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="auth-email-input"
                    placeholder="vous@exemple.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="auth-password-input"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="auth-submit-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={isLoading}
                >
                  {isLoading ? 'Chargement...' : authMode === 'login' ? 'Se connecter' : 'Créer mon compte'}
                </Button>
              </form>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
